/* eslint-disable max-lines */
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useStoreState, useStoreActions } from "easy-peasy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Auth } from "aws-amplify";
import { SHOW_WARNING as level, HR_APP_URL, SINGUP_URL } from "../constants";
import { login } from "../api/login";
import { verifyTokens } from "../api/verifyTokens";
import { settings, language, animations } from "../settings";
import {
  SignupTabs,
  Warning,
  SignInWithGoogle,
  SignInWithMicrosoft,
  FormField,
 Password } from "../components";
import { TwoColumnLayout } from "../templates";
import { redirectAfterLogin } from "../lib/redirect-after-login";
import { SmsMfa } from "../challenges/SmsMfa";
import { notifyError } from "../lib/notifications";
import { checkEmailValid } from "../helpers";
import { useFeatureFlag } from "../hooks/useFeatureFlag";

export const Login = () => {
  const setIsAuthenticated = useStoreActions(
    (actions) => actions.setIsAuthenticated,
  );
  const setChallenge = useStoreActions(
    (actions) => actions.setChallenge,
  );
  const challenge = useStoreState((state) => state.challenge);
  const { isEnabled: isGoogleOAuthEnabled, isLoading: isGoogleOAuthLoading } = useFeatureFlag("socialSignin");
  const { isEnabled: isMicrosoftOAuthEnabled, isLoading: isMicrosoftOAuthLoading } = useFeatureFlag("socialSigninForMicrosoft");
  const { isEnabled: isImpartnerLoginEnabled } = useFeatureFlag("impartnerLogin");
  
  const isOAuthEnabled = isGoogleOAuthEnabled || isMicrosoftOAuthEnabled;
  const isOAuthLoading = isGoogleOAuthLoading || isMicrosoftOAuthLoading;

  const history = useHistory();

  const [username, setUsername] = useState("");
  const usernameIsValidEmail = () => {
    return checkEmailValid(username);
  };
  const usernameIsNotValidEmail = () => {
    return !usernameIsValidEmail();
  };
  const params = new URLSearchParams(window.location.search);
  const isExistingUser = params.get("existing_user") === "1";

  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const setProfile = useStoreActions((actions) => actions.setProfile);

  const { animate, signUp } = settings;
  const { form } = animations;


  const signInCognito = async (e) => { 
    setLoading(true);
    e.preventDefault();

    try {

      console.log(username, password);
      const cognitoUser = await Auth.signIn(username.trim(), password);
      console.log(cognitoUser);
      // (Handle MFA/Challenges here as we discussed)

      // 2. IF a challenge wasn't returned, sign-in was a success
      if (cognitoUser.challengeName) {
        // Handle the challenge (e.g., redirect to MFA page)
        console.log(cognitoUser.challengeName);
        // ...
      } else {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        const refreshToken = session.getRefreshToken().getToken();
        console.log(idToken);
        console.log(accessToken);
        console.log(refreshToken);

        verifyTokens({ id_token: idToken, access_token: accessToken, refresh_token: refreshToken })
          .then((res) => {
            console.log(res);
            setIsAuthenticated(true);
            return redirectAfterLogin(history);
          })
          .catch((error) => {
            setLoading(false);
            if (!error.response) {
              return notifyError("Network error");
            }
            return notifyError(error.response.data.error);
          });
          
        return null;
      }
    } catch (error) {
      setLoading(false);
      console.error("Cognito error:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);

      if (error.code === "UserNotFoundException" || error.code === "NotAuthorizedException") {
        return notifyError("Incorrect username or password");
      }
      if (error.code === "UserNotConfirmedException") {
        return notifyError("Please confirm your account first");
      }
      return notifyError(error.message || "Something went wrong");
    }
  };

  const signIn = (e) => {
    setLoading(true);
    e.preventDefault();
    const body = { username, password };

    login(body)
      .then((res) => {
        if (res.data.challenge) {
          const challengePayload =
            res.data.challenge.name === "SOFTWARE_TOKEN_MFA"
              ? { ...res.data.challenge, password }
              : res.data.challenge;
          setChallenge(challengePayload);
          if (res.data.challenge.name === "NEW_PASSWORD_REQUIRED") {
            history.push("/set-new-password");
            return true;
          }
          if (res.data.challenge.name === "SOFTWARE_TOKEN_MFA") {
            history.push("/verify-2fa");
            return true;
          }
          return true;
        }

        if (res.data.mfa_setup_request) {
          setProfile({ email: username });
          history.push("/associate-software-token");
          return true;
        }

        setIsAuthenticated(true);
        return redirectAfterLogin(history);
      })
      .catch((error) => {
        setLoading(false);
        if (!error.response) {
          return notifyError("Network error");
        }
        if (error.response.status === 401) {
          return notifyError("Incorrect username or password");
        }

        return notifyError("Something went wrong");
      });

    return null;
  };

  const signUpUrl = () => {
    return SINGUP_URL === undefined ? `${HR_APP_URL}/signup` : SINGUP_URL;
  };

  const leftContent = (
    <div className="absolute inset-0">
      <picture>
        <source
          media="(min-width: 1024px)"
          srcSet="/design/images/login-page/sign-in-desktop.webp?v=3"
        />
        <img
          src="/design/images/login-page/sign-in-tablet.webp"
          alt="Sign in illustration"
          className="w-full h-full object-cover"
        />
      </picture>
      <div className="absolute z-10" style={{ top: '2.5rem', left: '2.5rem' }}>
        <img
          src="/design/images/login-page/breathe_logo_white.svg"
          alt="Breathe logo icon"
          className="h-8 w-9"
        />
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{language("title")}</title>
      </Helmet>
      {challenge && challenge.name === "SMS_MFA" && <SmsMfa session={challenge.session} username={challenge.username} />}
      {signUp && <SignupTabs />}
      <Warning level={level}>
        <p className="text-sm font-bold">{language("warningTitle")}</p>
        <p className="text-xs">{language("warningText")}</p>
      </Warning>
      {isExistingUser && (
        <div
          className="mb-5 mx-10 bg-yellow-100 border-t-4 border-yellow-500 rounded-b text-yellow-900 px-4 py-3 shadow-md"
          role="alert"
        >
          It looks like you already have a Breathe account. Please log in below.
        </div>
      )}

      <TwoColumnLayout leftContent={leftContent}>
        <div className="flex flex-col items-center md:items-start w-full mb-0">
          <h1 className="text-[30px] font-bold leading-[36px] login-text-foreground mb-0 font-rubik text-center md:text-left" style={{ fontWeight: 700, fontSize: "30px", lineHeight: "36px" }}>
            Welcome to Breathe
          </h1>
          <p className="login-text-muted mt-0 mb-6 font-rubik login-subtitle text-center md:text-left">
            Login to your account
          </p>
        </div>

        <motion.form
          variants={form}
          initial={animate ? "hidden" : ""}
          animate={animate ? "show" : ""}
          className="flex flex-col gap-6 items-center justify-center w-full"
        >
          <div className="flex flex-col gap-3 items-start w-full">
            <FormField
              id="email-input"
              type="email"
              label="Email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="me@example.com"
              error={username.length > 0 && !checkEmailValid(username)}
              autoComplete="off"
            />


            <div className="flex flex-col gap-2 items-start w-full relative">
              <div className="flex justify-between items-center w-full">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label
                  htmlFor="password-input"
                  className="text-sm font-normal leading-none login-text-foreground font-inter"
                >
                  Password
                </label>
                <div className="text-sm login-text-foreground text-right font-inter" aria-hidden="true">
                  {/* Spacer to maintain layout */}
                </div>
              </div>
              <Password
                password={password}
                setPassword={setPassword}
                variant="static"
                showLabel={false}
                id="password-input"
                label="Password"
              />
              <Link
                to={`/forgotten-password${username ? `?email=${username}` : ""}`}
                className="absolute top-0 right-0 text-sm login-text-foreground text-right underline font-inter"
              >
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              className={`w-full login-button rounded-md login-bg-primary text-white text-sm font-normal flex items-center justify-center font-inter ${
                usernameIsValidEmail() ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
              }`}
              onClick={isImpartnerLoginEnabled ? signInCognito : signIn}
              disabled={usernameIsNotValidEmail()}
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin aria-hidden />
              ) : (
                "Login"
              )}
            </button>
          </div>

          {!isOAuthLoading && isOAuthEnabled && (
            <div className="relative w-full py-2.5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t login-border-input" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-2 bg-white text-sm login-text-muted font-rubik">
                  Or continue with
                </span>
              </div>
            </div>
          )}

          {!isOAuthLoading && isOAuthEnabled && (
            <div className="flex flex-col gap-4 items-start w-full social-login-buttons">
              {isGoogleOAuthEnabled && <SignInWithGoogle />}
              {isMicrosoftOAuthEnabled && <SignInWithMicrosoft />}
            </div>
          )}

          <div className="flex gap-2.5 items-center justify-center w-full">
            <p className="text-sm login-text-foreground text-center font-rubik">
              Don&apos;t have an account?{" "}
              <a href={signUpUrl()} className="underline font-rubik">
                Sign up
              </a>
            </p>
          </div>
        </motion.form>
      </TwoColumnLayout>
    </>
  );
};
